import {
  Box,
  Button,
  Container,
  Header,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import { LineItemTypes } from '@parallel-fluidics/shared';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import OrgName from '../Display/orgName';
import { useActiveOrders, useProjectStatus, useShipDate } from './hooks';

function ProjectStatus({
  organization,
  projectNumber,
  shipDate,
  lineItemId,
  steps,
  type = null,
  quantity = null,
  quantityShipped = null,
}) {
  const { projectStatus, queryStatus } = useProjectStatus(
    steps,
    // pass null to skip fetching travelers for product type line items
    type === LineItemTypes.PRODUCT ? null : lineItemId,
  );
  const { status, statusText } = useMemo(() => {
    if (type === LineItemTypes.PRODUCT) {
      return { status: 'success', statusText: `${quantityShipped}/${quantity}` };
    }
    if (queryStatus === 'loading') {
      return { status: queryStatus };
    }
    try {
      const lastStep = steps[steps.length - 1];
      const lastStepStatus = projectStatus[steps.length - 1];
      return { status: 'success', statusText: `${lastStepStatus.passed}/${lastStep.goal}` };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      return { status: 'error' };
    }
  }, [projectStatus, queryStatus, steps, type, quantity, quantityShipped]);
  const shipDateText = useShipDate(shipDate);

  return (
    <div>
      <Box variant="awsui-key-label">
        {`[${projectNumber}] `}
        <OrgName orgId={organization} />
        {` - ${shipDateText}`}
      </Box>
      {status === 'loading' ? (
        <StatusIndicator type="loading">Loading...</StatusIndicator>
      ) : null}
      {status === 'error' ? (
        <StatusIndicator type="error">Unknown status</StatusIndicator>
      ) : null}
      {status === 'success' ? (
        <Box variant="awsui-value-large">
          {statusText}
        </Box>
      ) : null}
    </div>
  );
}

ProjectStatus.propTypes = {
  organization: PropTypes.string.isRequired,
  projectNumber: PropTypes.string.isRequired,
  shipDate: PropTypes.string.isRequired,
  lineItemId: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(PropTypes.shape({
    pPartId: PropTypes.string,
    pPartDocId: PropTypes.string,
    type: PropTypes.string,
    goal: PropTypes.number,
  })).isRequired,
  type: PropTypes.string,
  quantity: PropTypes.number,
  quantityShipped: PropTypes.number,
};

function Summary() {
  const activeOrders = useActiveOrders(5);
  const navigate = useNavigate();

  return (
    <Container
      fitHeight
      header={(
        <Header actions={<Button variant="normal" onClick={() => { navigate('/trackers/active'); }}>See all active orders</Button>}>
          Active orders
        </Header>
    )}
    >
      <SpaceBetween direction="vertical" size="l">
        {activeOrders.map((project) => (
          <ProjectStatus
            key={project.lineItemId}
            organization={project.organization}
            projectNumber={
              project.type === LineItemTypes.PRODUCT
                ? project.sku
                : project.projectNumber
            }
            lineItemId={project.lineItemId}
            shipDate={project.shipDate}
            steps={project.steps}
            type={project.type}
            quantity={project.quantity}
            quantityShipped={project.quantityShipped}
          />
        ))}
      </SpaceBetween>
    </Container>
  );
}

export default Summary;
